/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import { logger as log } from '@common/logger';

import { Graphql } from '@common/graphql';
import { Sttp } from '@common/sttp';
import { usePolicyId } from '../../provider';
import { apiConfig } from '@/config/apiConfig';

import { GET_TRANSACTION_LIST } from '@pages/ActualDetailPage/api/graphql';
import config from '@pages/ActualDetailPage/config/featuresSection.config.json';
import { themeConfig } from '@/providers/theme';
import { toHeaderCase } from 'js-convert-case';
import moment from 'moment';

import { AppNavigatorService } from '@/AppNavigator/services';
import { vappContainer } from '@/application';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

export interface ITransactionListPluginMethods extends IPluginMethodMap {
  getTransactionList(args?: any): Promise<any>;
}

export class TransactionListPlugin implements IAgentPlugin {
  readonly methods: ITransactionListPluginMethods;
  constructor() {
    this.methods = {
      getTransactionList: this.getTransactionList.bind(this),
    };
  }

  async getTransactionList(args?: any) {
    // await new Promise((r) => setTimeout(r, 2000));
    return await this.props(args);
    // return { ...transactionActualDetailConfig };
  }
  //Graphql Query example
  async props(args?: any) {
    const { policyNumber, userVid } = usePolicyId();
    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      const token = loginSrv()?.getToken();
      // const token = null;
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      const res = await client
        .query(GET_TRANSACTION_LIST, {
          input: {
            policyId: policyNumber,
          },
          input1: {
            limit: 100,
            where: [
              {
                column: 'txnOwner',
                op: 'Equal',
                value: [userVid],
              },
              {
                column: 'type',
                op: 'In',
                value: ['POLICY_PURCHASE_FLOW', 'KYC_SUBMISSION'],
              },
              {
                column: 'txnSetId',
                op: 'In',
                value: [policyNumber, null],
              },
            ],
          },
        })
        .toPromise();
      if (res?.data?.getTxnList) {
        let _txtResult = res?.data?.getTxnList?.data;
        return {
          accordionItems: this.getAccordionItems(_txtResult),
          timelineData: res?.data?.getTxnList,
        };
      }
    } catch (err: any) {
      // log.log('result of capsules=', res?.data?.capsules);
      log.error('error in capsules=', err?.message);
      throw err;
    }
  }

  private getAccordionItems(txList: any) {
    let items: any = [];
    if (txList?.length > 0) {
      let timelineDetails = this.getTimeLine(txList);
      items.push({
        type: 'timeline',
        title: config?.timelineDetails?.title,
        data: [...timelineDetails],
        lineColor: themeConfig?.colors?.orangePrimary,
        horizontalStepperColors: {
          DONE: themeConfig?.colors?.orangeSecondary,
          UNDONE: themeConfig?.colors?.orangeSecondary,
          LAST: themeConfig?.colors?.orangeSecondary,
          CURRENT: themeConfig?.colors?.orangeSecondary,
        },
      });
    }
    return items;
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) &&
      data?.forEach((element) => {
        _temp.push({
          key: element?.id,
          headline: toHeaderCase(element?.action ?? ''),
          headlineBackgroundColor: themeConfig?.colors?.orangePrimary,
          children: this.getChildren(element),
        });
      });

    return _temp;
  }

  private getChildren(data: any) {
    let _temp: any = [];
    _temp.push({
      itemId: data?.id,
      multiColList: this.getProvenanceAccordion(data),
      title: toHeaderCase(data?.action ?? ''),
      subtitle: data?.when ? moment(parseInt(data?.when)).format('LLL') : null,
    });

    return _temp;
  }

  private getProvenanceAccordion(res: any) {
    let _temp: any = [];
    if (res) {
      let _list: any = [];
      let _date: any = [];
      let _location: any = [];
      if (res?.performedBy?.firstName) {
        _list.push({
          title: toHeaderCase('by'),
          subTitle: `${toHeaderCase(
            res?.performedBy?.firstName
          )} ${toHeaderCase(res?.performedBy?.lastName)}`,
          icon: config?.orgIcon,
        });
      }
      if (res?.location) {
        _location.push({
          title: toHeaderCase('at'),
          subTitle: res?.location ?? '-',
          icon: config?.locationIcon,
        });
      }
      if (res?.when) {
        _date.push({
          title: toHeaderCase('on'),
          subTitle: res?.when ? moment(parseInt(res?.when)).format('LLL') : '-',
          icon: config?.dateIcon,
        });
      }
      _temp = [..._list, ..._location, ..._date];
    }
    return {
      numberOfColumns: '2',
      data: _temp,
    };
  }
}
